import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { DisablableOption } from "@models";
import { SourceBook } from "@dndModels";

import DandDAPI from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import LoadingBox from "@components/input/LoadingBox";
import { SOURCE_BOOK_TYPES } from "@dndModels/Constants";
import { SourceBookType } from "@dndModels/Types";

const TYPE_PRIORITY_MAP = {
  "Source Book": 0,
  "Setting": 1,
  "Adventure": 2,
  "Homebrew": 3,
  "Unearthed Arcana": 4
};

interface Props {
  name?: string;
  id?: string;
  ids?: string[];
  type?: SourceBookType | "";
  types?: SourceBookType[];
  multiple?: boolean;
  disabled?: boolean;
  archives?: boolean;
  options?: DisablableOption[];
  onChange?: (type: SourceBookType, id: string) => void;
  onChangeMultiple?: (types: SourceBookType[], ids: string[]) => void;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function SelectSourceBookBox({
  name = "",
  id = "",
  ids = [],
  type,
  types,
  multiple = false,
  disabled = false,
  archives,
  options,
  onChange,
  onChangeMultiple
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [run_filter, setRunFilter] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [is_archived, setIsArchived] = useState(archives === undefined ? false : archives);
  const [source_books, setSourceBooks] = useState<SourceBook[]>([]);
  const [available_types, setAvailableTypes] = useState<string[]>([]);
  const [prev_types, setPrevTypes] = useState<SourceBookType[]>([]);
  const [filtered, setFiltered] = useState<DisablableOption[]>([]);

  const load = useCallback( async () => {
    let sbs = api.source_books;
    let ts: string[] = [];
    // debugger;
    if (options) {
      const sb_ids = options.map(o => o.id);
      sbs = sbs.filter(o => sb_ids.includes(o._id));
    }
    sbs = sbs.sort((a, b) => {
      if (a.source_book_type === b.source_book_type) {
        return a.name.localeCompare(b.name);
      } else {
        return TYPE_PRIORITY_MAP[a.source_book_type] - TYPE_PRIORITY_MAP[b.source_book_type];
      }
    });
    ts = common_util.unique_array(sbs.flatMap(o => o.source_book_type));
    ts = SOURCE_BOOK_TYPES.filter(o => ts.includes(o)); // put it in the right order
    setSourceBooks(sbs);
    setAvailableTypes(ts); 
    setRunFilter(true);
  }, [options]);
  
  const do_filter = useCallback(() => {
    let sbs = source_books;
    if (prev_types.length > 0) {
      sbs = api.filter("source_book", sbs, { source_book_type: { "in": prev_types } });
    }
    sbs = api.filter("source_book", sbs, { is_archived });
    setFiltered(sbs.map(o => {
      const opt: DisablableOption = {
        id: o._id,
        text: `${o.use_name} (${o.abbreviation})`
      };
      return opt;
    }))
  }, [source_books, prev_types, is_archived]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (run_filter) {
      setRunFilter(false);
      do_filter();
    }
  }, [run_filter, do_filter]);

  useEffect(() => {
    if (type) {
      if (prev_types.length !== 1 || prev_types[0] !== type) {
        setPrevTypes([type]);
        setRunFilter(true);
      }
    } else if (types) {
      if (!common_util.listsMatch(types, prev_types)) {
        setPrevTypes(types);
        setRunFilter(true);
      }
    }
  }, [type, types, prev_types]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  if (!fetched) {
    return (<LoadingBox />);
  } else {
    if (multiple && onChangeMultiple) {
      return (
        <Grid container spacing={0} direction="row">
          { name !== "" &&
            <Grid
              className="centered header"
              size={{
                xs: 4
              }}>
              { name }
            </Grid>
          }
          { available_types.length > 1 &&
            <Grid
              size={{
                xs: name === "" ? 6 : 4
              }}>
              <SelectStringBox
                name="Source Types"
                disabled={disabled}
                values={types} 
                options={available_types}
                multiple
                onChange={(changed: SourceBookType[]) => {
                  setReloading(true);
                  onChangeMultiple(changed, ids);
                }}
              />
            </Grid>
          }
          <Grid
            size={{
              xs: name === "" ? (available_types.length > 1 ? 6 : 12) : (available_types.length > 1 ? 4 : 8)
            }}>
            { !reloading && 
              <SelectStringBox
                name="Source Book"
                values={ids} 
                options={filtered}
                disabled={disabled}
                multiple
                // log_it
                onChange={(changed: string[]) => {
                  onChangeMultiple(prev_types, changed);
                }}
              />
            }
          </Grid>
          { archives === undefined && 
            <Grid
              size={{
                xs: 12
              }}>
              <CheckBox
                name="Check Archives"
                value={is_archived}
                onChange={(changed: boolean) => {
                  setIsArchived(changed);
                  setReloading(true);
                }}
              />
            </Grid>
          }
        </Grid>
      );
    } else if (onChange) {
      return (
        <Grid container spacing={0} direction="row">
          { name !== "" &&
            <Grid
              className="centered header"
              size={{
                xs: 4
              }}>
              { name }
            </Grid>
          }
          { available_types.length > 1 &&
            <Grid
              size={{
                xs: name === "" ? 6 : 4
              }}>
              <SelectStringBox
                name="Source Type"
                disabled={disabled}
                value={type} 
                options={available_types}
                onChange={(changed: SourceBookType) => {
                  setReloading(true);
                  onChange(changed, id);
                }}
              />
            </Grid>
          }
          <Grid
            size={{
              xs: name === "" ? (available_types.length > 1 ? 6 : 12) : (available_types.length > 1 ? 4 : 8)
            }}>
            { !reloading && type && api.source_books &&
              <SelectStringBox
                name="Source Book"
                value={id} 
                options={filtered}
                disabled={disabled}
                // log_it
                onChange={(changed: string) => {
                  onChange(prev_types[0], changed);
                }}
              />
            }
          </Grid>
          { archives === undefined && 
            <Grid
              size={{
                xs: 12
              }}>
              <CheckBox
                name="Check Archives"
                value={is_archived}
                onChange={setIsArchived}
              />
            </Grid>
          }
        </Grid>
      );
    }
    return <></>;
  }
}
